import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  applyPreset as resolvePreset,
  offerVlansToOnu,
  oltFamily,
  suggestOnuType,
  type AuthorizePreset,
  type CliActionResult,
  type KnownOnuRecord,
  type Olt,
  type OltVendor,
  type OltVlanListResponse,
  type Page,
  type PonPort,
  type Topology,
  type VlanAddressing,
  type VlanMode,
} from '@simpleacs/shared';
import { api, ApiRequestError } from '../lib/api';
import { onuPath } from '../lib/links';
import { SplitterPortGrid } from './device/SplitterPortGrid';
import { Button, ErrorNotice, Field, Input, PageHeader, Pill, Spinner } from '../components/ui';

/**
 * Authorise a discovered ONU — a full page, not a modal.
 *
 * It carries fifteen fields across model, service, management and placement, and
 * a modal cannot hold that without hiding half of it (the same reason org
 * migration is its own page). So it wears the platform's stepped-wizard chrome:
 * labelled steps then a Review, exactly like the OMCI/TR-069 management-mode
 * dialog, with the heavy auto-fill kept so a pole tech taps almost nothing.
 *
 * The row that opens it hands the discovered ONU across in router state; a
 * direct visit or a refresh re-reads the chassis's waiting list to find it, so
 * the page stands on its own URL.
 */

interface UnconfiguredOnu {
  ponPort: string;
  serial: string;
  state: string;
  detectedModel?: string;
  suggestedOnuId: number;
  usedIds: number[];
  knownElsewhere: KnownOnuRecord | null;
  /**
   * The ONU types this ZTE chassis DEFINES, the ones its ONUs are declared as
   * first. A ZTE declares an ONU by a chassis-defined type and refuses any
   * other — so this, not the org's hardware library, is what the type step
   * offers there. Absent on Huawei (which declares by profile) and on a read
   * that failed, where the library stands in as before.
   */
  chassisOnuTypes?: string[];
}
interface UnconfiguredRow extends UnconfiguredOnu {
  oltId: string;
  oltName: string;
}
interface HardwareModel {
  id: string;
  name: string;
  vendor: string | null;
  onuCount: number;
}
interface SpeedProfileOption {
  id: string;
  name: string;
  direction: 'up' | 'down';
  maxKbps: number | null;
  notes: string | null;
  writtenTo: Array<{ oltId: string }>;
}

function speedLabel(kbps: number | null): string {
  if (kbps === null || kbps <= 0) return '';
  if (kbps >= 1_000_000) {
    const gig = kbps / 1_048_576;
    return ` · ${gig >= 0.95 && gig <= 1.05 ? 1 : Math.round(gig * 10) / 10}G`;
  }
  return ` · ${Math.round(kbps / 1024)}M`;
}
function vendorLabel(vendor: string | null | undefined): string {
  if (vendor === 'zte') return 'ZTE';
  if (vendor === 'huawei') return 'Huawei';
  return 'OLT';
}

/** The URL an unconfigured row links to, target handed across in router state. */
export function authorizePath(target: UnconfiguredRow): string {
  return `/olts/${encodeURIComponent(target.oltId)}/authorize/${encodeURIComponent(target.serial)}`;
}

/** Route wrapper: resolves the discovered ONU, then hands it to the wizard. */
export function AuthorizeOnu() {
  const { oltId = '', serial = '' } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const passed =
    (location.state as {
      target?: UnconfiguredRow;
      vendor?: string | null;
      model?: string | null;
      refusals?: Olt['refusals'];
    } | null) ?? null;
  const havePassed = passed?.target?.serial === serial;

  // The OLT itself is always read: it is a DB row, and it carries the model
  // and the server-resolved `refusals` the wizard gates on. The row that
  // opened this hands both across in router state for the first paint; this
  // read is what keeps them true. Only the unconfigured list below is guarded
  // — that one is a live CLI read of the chassis, too heavy to run when the
  // row already has it.
  const oltQuery = useQuery({
    queryKey: ['olt', oltId],
    queryFn: () => api.get<Olt>(`/olts/${encodeURIComponent(oltId)}`),
    enabled: Boolean(oltId),
  });
  const unconfQuery = useQuery({
    queryKey: ['olt', oltId, 'unconfigured'],
    queryFn: () => api.get<{ items: UnconfiguredOnu[] }>(`/olts/${encodeURIComponent(oltId)}/unconfigured`),
    enabled: !havePassed && Boolean(oltId),
  });

  const target: UnconfiguredRow | null = havePassed
    ? passed!.target!
    : (() => {
        const found = unconfQuery.data?.items.find((u) => u.serial === serial);
        return found ? { ...found, oltId, oltName: oltQuery.data?.name ?? oltId } : null;
      })();
  const vendor = havePassed ? (passed?.vendor ?? oltQuery.data?.vendor ?? null) : (oltQuery.data?.vendor ?? null);
  const model = oltQuery.data?.model ?? passed?.model ?? null;
  const refusals = oltQuery.data?.refusals ?? passed?.refusals;

  if (!target) {
    const loading = unconfQuery.isLoading || oltQuery.isLoading;
    return (
      <div className="mx-auto max-w-3xl">
        <PageHeader title="Authorise ONU" />
        <div className="rounded-xl border border-line bg-surface p-8 text-center">
          {loading ? (
            <span className="inline-flex items-center gap-3 text-sm text-muted">
              <Spinner /> Reading what {serial ? serial : 'the ONU'} needs…
            </span>
          ) : (
            <>
              <p className="text-sm text-ink">This ONU is no longer waiting on the chassis.</p>
              <p className="mt-1 text-sm text-muted">
                It may have been authorised already, or it dropped off the PON. Go back to the
                awaiting list to check.
              </p>
              <Button className="mt-4" onClick={() => navigate(-1)}>
                Back
              </Button>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <AuthorizeWizard
      oltId={oltId}
      oltName={target.oltName}
      vendor={vendor}
      model={model}
      refusals={refusals}
      target={target}
    />
  );
}

function AuthorizeWizard({
  oltId,
  oltName,
  vendor,
  model,
  refusals,
  target,
}: {
  oltId: string;
  oltName: string;
  vendor: string | null;
  model: string | null;
  /** What this chassis generation refuses, resolved by the server (GET /olts/:id). */
  refusals: Olt['refusals'];
  target: UnconfiguredRow;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const onuId = String(target.suggestedOnuId);
  const [step, setStep] = useState(0);
  const [onuType, setOnuType] = useState('');
  const [typeTouched, setTypeTouched] = useState(false);
  const [customType, setCustomType] = useState('');
  const [name, setName] = useState('');
  const [upProfile, setUpProfile] = useState('');
  const [downProfile, setDownProfile] = useState('');
  const [dataVlan, setDataVlan] = useState('');
  const [mgmtVlan, setMgmtVlan] = useState('');
  // Set once the operator has chosen a management VLAN themselves — including
  // choosing NONE, which the auto-fill below would otherwise undo.
  const [mgmtTouched, setMgmtTouched] = useState(false);
  const [vlanMode, setVlanMode] = useState<VlanMode>('route');
  const [addressing, setAddressing] = useState<VlanAddressing>('dhcp');
  // The PPPoE account, optional: with it the CPE's WAN is created the first
  // time it informs; without it the ONU's page asks, as before. Never in a
  // preset — it is the subscriber's, not the site's.
  const [pppoeUser, setPppoeUser] = useState('');
  const [pppoePass, setPppoePass] = useState('');
  /**
   * The unit is still authorised on another slot of this chassis (a recovered
   * ONU being reinstalled for a new subscriber). The API refuses that by name
   * and says which flag reuses it; "Authorise afresh" resends with the flag,
   * and the API deauthorises the old slot first, then authorises here afresh.
   */
  const [elsewhere, setElsewhere] = useState<string | null>(null);
  /*
    A bridged subscriber has no addressing here — the router behind the ONU
    gets its own address (operator, 2026-10-14: "a bridging onu should not
    have addressing mode or firewall options"). The field is hidden and DHCP,
    the platform's neutral placeholder for a bridged VLAN, is what is sent.
  */
  const effectiveAddressing: VlanAddressing = vlanMode === 'bridge' ? 'dhcp' : addressing;
  const accountGiven = effectiveAddressing === 'pppoe' && pppoeUser.trim() !== '' && pppoePass !== '';
  const accountHalf = effectiveAddressing === 'pppoe' && !accountGiven && (pppoeUser.trim() !== '' || pppoePass !== '');
  const [zoneId, setZoneId] = useState('');
  const [splitterId, setSplitterId] = useState('');
  const [splitterPort, setSplitterPort] = useState('');
  const [presetId, setPresetId] = useState('');
  // TR-069 is the platform default — an ONU is authorised onto the ACS unless
  // the operator explicitly picks OMCI. Leaving it unset was the trap: the ONU
  // came up with a management IP and no ACS pointer, and never informed.
  const [managementMode, setManagementMode] = useState<'tr069' | 'omci' | ''>('tr069');
  const [presetNotes, setPresetNotes] = useState<string[]>([]);
  const [savingPreset, setSavingPreset] = useState(false);
  const [presetName, setPresetName] = useState('');
  const [error, setError] = useState<string | null>(null);

  const models = useQuery({
    queryKey: ['onu-hardware'],
    queryFn: () => api.get<{ items: HardwareModel[] }>('/onu-hardware'),
  });
  const speeds = useQuery({
    queryKey: ['speed-profiles'],
    queryFn: () => api.get<{ items: SpeedProfileOption[] }>('/speed-profiles'),
  });
  const speedItems = speeds.data?.items ?? [];
  const presets = useQuery({
    queryKey: ['onu-presets'],
    queryFn: () => api.get<Page<AuthorizePreset>>('/onu-presets'),
  });
  const byUse = (a: AuthorizePreset, b: AuthorizePreset) =>
    (b.useCount ?? 0) - (a.useCount ?? 0) || a.name.localeCompare(b.name);
  const ownVendor = (presets.data?.items ?? []).filter((p) => !p.vendor || p.vendor === vendor).sort(byUse);
  const otherVendor = (presets.data?.items ?? []).filter((p) => p.vendor && p.vendor !== vendor).sort(byUse);

  const topology = useQuery({ queryKey: ['topology'], queryFn: () => api.get<Topology>('/topology') });
  const zones = topology.data?.zones ?? [];
  const splitters = (topology.data?.splitters ?? []).filter((sp) => !zoneId || sp.zoneId === zoneId);
  const chosenSplitter = splitters.find((sp) => sp.id === splitterId);

  const ports = useQuery({
    queryKey: ['olt', oltId, 'pon-ports'],
    queryFn: () => api.get<Page<PonPort>>(`/olts/${encodeURIComponent(oltId)}/pon-ports`),
  });

  const oltVlans = useQuery({
    queryKey: ['olt', oltId, 'vlans'],
    queryFn: () => api.get<OltVlanListResponse>(`/olts/${encodeURIComponent(oltId)}/vlans`),
  });
  const vlanItems = oltVlans.data?.items ?? [];
  const vlanOffer = offerVlansToOnu(vlanItems, { vlan1Enabled: oltVlans.data?.vlan1Selectable === true });
  const subscriberVlans = vlanOffer.offered;
  const managementVlans = vlanItems.filter((v) => v.usedForMgmt);

  // --- Auto-fill: every field with one obvious value fills itself, once, until
  // the operator touches it. A pole tech taps almost nothing. ---
  useEffect(() => {
    if (dataVlan || subscriberVlans.length === 0) return;
    setDataVlan(String(subscriberVlans[0]!.vlanId));
  }, [subscriberVlans, dataVlan]);
  useEffect(() => {
    if (mgmtVlan || mgmtTouched || managementVlans.length === 0) return;
    setMgmtVlan(String(managementVlans[0]!.vlanId));
  }, [managementVlans, mgmtVlan, mgmtTouched]);
  useEffect(() => {
    for (const [direction, value, set] of [
      ['up', upProfile, setUpProfile],
      ['down', downProfile, setDownProfile],
    ] as const) {
      if (value) continue;
      const options = speedItems.filter((p) => p.direction === direction);
      const preferred = options.find((p) => /^M1000\b/i.test(p.name));
      if (preferred) set(preferred.id);
    }
  }, [speedItems, upProfile, downProfile]);

  const suggestion = suggestOnuType(target.serial, models.data?.items ?? [], target.detectedModel);
  /**
   * What the type step offers, and what it opens on.
   *
   * On a ZTE the chassis's own defined types are the whole menu: the
   * operator's C650 reports a waiting unit as HG8546M, defines no such
   * type, and declares all 105 of its Huawei units as EG8141A5 — offering
   * the library's HG8546M there had the chassis refuse the declaration
   * (2026-09-19). The default is the reported model when the chassis defines
   * it, else the type most of this chassis's ONUs are declared as, which the
   * server lists first; the hint says which of the two it was.
   */
  const chassisTypes = target.chassisOnuTypes ?? null;
  const chassisDefault = chassisTypes
    ? (chassisTypes.find((t) => t.toLowerCase() === (target.detectedModel ?? '').toLowerCase()) ??
      chassisTypes.find((t) => t === suggestion.match?.name) ??
      chassisTypes[0] ??
      null)
    : null;
  const typeHint = chassisTypes
    ? chassisDefault && target.detectedModel && chassisDefault.toLowerCase() === target.detectedModel.toLowerCase()
      ? `The chassis reports this ONU as ${target.detectedModel} and defines that type.`
      : target.detectedModel
        ? `The chassis reports this ONU as ${target.detectedModel} but defines no such type; ${chassisDefault ?? 'the first listed'} is what its own ONUs are declared as.`
        : `The types ${oltName} defines, the one its ONUs use first.`
    : suggestion.reason;
  useEffect(() => {
    if (typeTouched) return;
    if (chassisDefault) {
      setOnuType(chassisDefault);
      return;
    }
    if (suggestion.match) setOnuType(suggestion.match.name);
  }, [suggestion, typeTouched, chassisDefault]);

  const chosenType = (customType.trim() || onuType).trim();
  const idTaken = target.usedIds.includes(Number(onuId));

  const thisPort = ports.data?.items.find((p) => p.port === target.ponPort);
  const capacity = thisPort?.maxOnus ?? 128;
  const used = target.usedIds.length;
  const free = Math.max(capacity - used, 0);
  const pct = capacity > 0 ? Math.min(Math.round((used / capacity) * 100), 100) : 0;

  const applyPreset = (p: AuthorizePreset) => {
    setPresetId(p.id);
    const { values, warnings } = resolvePreset(p, {
      vendor,
      subscriberVlans: subscriberVlans.map((v) => v.vlanId),
      managementVlans: managementVlans.map((v) => v.vlanId),
      onuTypes: (models.data?.items ?? []).map((m) => m.name),
      speedProfileIds: speedItems.map((sp) => sp.id),
      zoneIds: zones.map((z) => z.id),
    });
    setPresetNotes(warnings);
    if (values.onuType !== undefined) {
      const known = (models.data?.items ?? []).some((m) => m.name === values.onuType);
      setTypeTouched(true);
      if (known) {
        setOnuType(values.onuType);
        setCustomType('');
      } else {
        setCustomType(values.onuType);
      }
    }
    if (values.upstreamProfileId !== undefined) setUpProfile(values.upstreamProfileId);
    if (values.downstreamProfileId !== undefined) setDownProfile(values.downstreamProfileId);
    setVlanMode(values.vlanMode);
    setAddressing(values.addressing);
    if (values.dataVlan !== undefined) setDataVlan(String(values.dataVlan));
    if (values.managementVlan !== undefined) setMgmtVlan(String(values.managementVlan));
    if (values.managementMode !== undefined) setManagementMode(values.managementMode);
    if (values.zoneId !== undefined) {
      setZoneId(values.zoneId);
      setSplitterId('');
      setSplitterPort('');
    }
  };

  const savePreset = useMutation({
    mutationFn: () =>
      api.post<AuthorizePreset>('/onu-presets', {
        name: presetName.trim(),
        onuType: chosenType || null,
        upstreamProfileId: upProfile || null,
        downstreamProfileId: downProfile || null,
        vlanMode,
        addressing: effectiveAddressing,
        dataVlan: dataVlan ? Number(dataVlan) : null,
        managementVlan: mgmtVlan ? Number(mgmtVlan) : null,
        vendor: vendor === 'zte' || vendor === 'huawei' ? vendor : null,
        zoneId: zoneId || null,
        managementMode: managementMode || null,
      }),
    onSuccess: async (created) => {
      setSavingPreset(false);
      setPresetName('');
      setPresetId(created.id);
      await presets.refetch();
    },
    onError: (err) => setError(err instanceof Error ? err.message : 'Could not save the preset'),
  });

  const path = `/olts/${encodeURIComponent(oltId)}/unconfigured/authorize`;
  const body = () => ({
    ponPort: target.ponPort,
    serial: target.serial,
    onuId: Number(onuId),
    onuType: chosenType || undefined,
    name: name.trim() || undefined,
    upstreamProfileId: upProfile || undefined,
    downstreamProfileId: downProfile || undefined,
    dataVlan: dataVlan ? Number(dataVlan) : undefined,
    managementVlan: mgmtVlan ? Number(mgmtVlan) : undefined,
    vlanMode,
    addressing: effectiveAddressing,
    ...(accountGiven ? { pppoeUsername: pppoeUser.trim(), pppoePassword: pppoePass } : {}),
    zoneId: zoneId || undefined,
    splitterId: splitterId || undefined,
    splitterPort: splitterPort ? Number(splitterPort) : undefined,
    presetId: presetId || undefined,
    managementMode: managementMode || undefined,
    dryRun: false,
  });

  /**
   * `background` means the API answers as soon as the ONU row exists and drives
   * the chassis behind it, so this leaves for the subscriber's page immediately
   * rather than holding the wizard for the length of a CLI session.
   */
  const apply = useMutation({
    mutationFn: (opts: { deauthorizeElsewhere?: boolean } = {}) =>
      api.post<CliActionResult & { onu?: { id: string; number: number | null } }>(path, {
        ...body(),
        background: true,
        ...(opts.deauthorizeElsewhere ? { deauthorizeElsewhere: true } : {}),
      }),
    onSuccess: async (r) => {
      // Prefix keys, so both the fleet and single-OLT variants refresh: the
      // awaiting list is ['unconfigured', oltId|'fleet', …] and the ONU list is
      // ['onus', oltId|'fleet', …]. The just-authorised ONU leaves the waiting
      // list and joins the inventory when the operator comes back to them.
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['unconfigured'] }),
        queryClient.invalidateQueries({ queryKey: ['onus'] }),
      ]);
      if (r.onu) {
        /*
          Auto-resync, so a routed-DHCP subscriber gets an address without the
          operator resyncing by hand. A bare ZTE authorise writes the chassis
          side (flows, GEMs, service-ports, the ip-host) but not the ONU-side
          OMCI stack a routed WAN needs to complete DHCP — the flow→switch bind,
          VEIP and ACS target — which only the resync rebuilds. Fired against the
          SAME endpoint the manual Resync button uses (the authorise already
          stored this ONU's VLAN/port intent, so the plan has something to push).
          The OLT lock serialises its write behind the authorise write, and it
          defers via pending-confirm if the ONU has not ranged yet.

          Fire-and-forget, not awaited: the endpoint reads the chassis before it
          answers, and blocking on that would hold the operator off the
          subscriber's page. Client-side navigation keeps the request alive; a
          failure only means the operator resyncs by hand as before, and the job
          reports on the ONU page like any resync.
        */
        void api
          .post(`/olts/${encodeURIComponent(oltId)}/onus/${encodeURIComponent(r.onu.id)}/resync`, {})
          .catch(() => undefined);
        navigate(onuPath(r.onu));
      } else navigate(-1);
    },
    onError: (err) => {
      const details = err instanceof ApiRequestError ? (err.details as { deauthorizeElsewhere?: boolean } | undefined) : undefined;
      if (err instanceof ApiRequestError && err.status === 409 && details?.deauthorizeElsewhere) {
        setElsewhere(err.message);
        setError(null);
        return;
      }
      setElsewhere(null);
      setError(err instanceof Error ? err.message : 'Could not authorise');
    },
  });

  const steps: Array<{ key: string; title: string }> = [
    { key: 'place', title: 'Where it goes' },
    { key: 'type', title: 'What it is' },
    { key: 'service', title: 'Service' },
    { key: 'mgmt', title: 'Management' },
    ...(zones.length > 0 ? [{ key: 'location', title: 'Placement' }] : []),
    { key: 'review', title: 'Review' },
  ];
  const current = steps[Math.min(step, steps.length - 1)]!;

  // TR-069 (the default) cannot be written without a management VLAN: the
  // chassis accepts the change and the ONU never informs. Block it in the wizard
  // instead of letting the authorise round-trip to a server 400 — on an OLT that
  // carries no management VLAN the operator switches to "OMCI only" here.
  const tr069NeedsVlan = managementMode === 'tr069' && !mgmtVlan;

  /*
    Until 2026-09-19 this wizard gated a ZTE C650 on the OLT DTO's
    `refusals.onu_ip_hosts`: a management VLAN or a routed subscriber VLAN was
    refused there before the round-trip, so that chassis could only take a
    bridged, unmanaged, OMCI-only authorise ("unable to setup in routing mode,
    only bridge" — the operator, on the first unit waiting on Lagos). The
    server composes the C6xx's own block now — the management write's
    `wan-ip … host 2` path addresses the ONU and the CPE's TR-069 WAN carries
    the subscriber — so every choice here is offered on every generation, and
    the C650 is told what its routed WAN means in the dry run's own words.
  */
  const isC650 = oltFamily(vendor as OltVendor, model) === 'c6xx';

  // Each step enforces only its own guard; Review enforces all of them.
  const canAdvance =
    (current.key !== 'place' || !idTaken) &&
    (current.key !== 'type' || Boolean(chosenType)) &&
    (current.key !== 'service' || (Boolean(dataVlan) && !accountHalf)) &&
    (current.key !== 'mgmt' || !tr069NeedsVlan);

  const goBack = () => {
    if (step === 0) navigate(-1);
    else setStep((s) => s - 1);
  };

  // What a routed subscriber means on a C650, said where the choice is made:
  // the chassis carries no subscriber WAN on that generation; the CPE does,
  // over TR-069, once it informs — exactly how SmartOLT built every unit there.
  const c650RoutedNotice = (
    <div className="rounded-lg border border-line bg-surface-2 px-4 py-3 text-sm text-muted">
      <p className="font-medium text-ink">{oltName} is a ZTE C650: the routed WAN lives on the ONU, not the chassis.</p>
      <p className="mt-1">
        The chassis gets the VLAN, the T-CONT/GEM and the service-port; the ONU is pointed at the ACS by
        the management step; and its own WAN — {addressing === 'pppoe' ? 'PPPoE' : addressing === 'static' ? 'a static address' : 'DHCP'} on VLAN{' '}
        {dataVlan || '…'} — is pushed over TR-069 the first time it informs.
        {addressing === 'pppoe' ? (accountGiven ? ` It dials as ${pppoeUser.trim()}.` : ' The PPPoE account is filled in on the ONU’s page.') : ''}
        {addressing === 'static' ? ' The address is filled in on the ONU’s page.' : ''}
      </p>
    </div>
  );

  return (
    <div className="mx-auto max-w-3xl">
      <PageHeader
        title={`Authorise ${target.serial}`}
        description={`Waiting on ${oltName} at ${target.ponPort}. Nothing is written until you apply.`}
        actions={
          <Button variant="ghost" onClick={() => navigate(-1)}>
            Back
          </Button>
        }
      />

      {/* The stepped-wizard pill row — the same look the management-mode dialog
          wears, lifted out of WizardDialog because this is a page, not a modal. */}
      <div className="mb-4 flex flex-wrap gap-1">
        {steps.map((s, i) => (
          <span
            key={s.key}
            className={`rounded-full px-2 py-0.5 text-[11px] ${
              i === step
                ? 'bg-primary-soft text-primary'
                : i < step
                  ? 'text-muted'
                  : 'text-faint'
            }`}
          >
            {s.title}
          </span>
        ))}
      </div>

      <div className="rounded-xl border border-line bg-surface">
        <div className="space-y-4 p-5">
          {current.key === 'place' && (
            <>
              {vendor === 'huawei' && (
                <p className="rounded-lg border border-warn/40 bg-warn-soft px-4 py-3 text-sm text-warn">
                  A Huawei OLT cannot configure WiFi over OMCI — the chassis has no ONT wifi command.
                  This ONU's SSIDs and passphrases stay whatever the device holds until it is put on
                  TR-069.
                </p>
              )}

              {((presets.data?.items?.length ?? 0) > 0 || savingPreset) && (
                <div className="flex flex-wrap items-end gap-3 rounded-lg border border-line p-4">
                  <Field label="Preset" hint="Fills the form; every field stays yours to change">
                    <select
                      className="input min-w-44"
                      value={presetId}
                      onChange={(e) => {
                        const p = presets.data?.items.find((row) => row.id === e.target.value);
                        if (p) applyPreset(p);
                        else {
                          setPresetId('');
                          setPresetNotes([]);
                        }
                      }}
                    >
                      <option value="">No preset</option>
                      {ownVendor.length > 0 && (
                        <optgroup label={`For this ${vendorLabel(vendor)} OLT`}>
                          {ownVendor.map((p) => (
                            <option key={p.id} value={p.id}>
                              {p.name}
                            </option>
                          ))}
                        </optgroup>
                      )}
                      {otherVendor.length > 0 && (
                        <optgroup label="Built on another vendor">
                          {otherVendor.map((p) => (
                            <option key={p.id} value={p.id}>
                              {p.name} ({vendorLabel(p.vendor)})
                            </option>
                          ))}
                        </optgroup>
                      )}
                    </select>
                  </Field>
                  {savingPreset ? (
                    <span className="flex items-end gap-2">
                      <Field label="Save these choices as">
                        <Input
                          value={presetName}
                          autoFocus
                          maxLength={60}
                          placeholder="Standard residential"
                          onChange={(e) => setPresetName(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && presetName.trim()) savePreset.mutate();
                            if (e.key === 'Escape') setSavingPreset(false);
                          }}
                        />
                      </Field>
                      <Button
                        size="sm"
                        variant="primary"
                        disabled={!presetName.trim()}
                        loading={savePreset.isPending}
                        onClick={() => savePreset.mutate()}
                      >
                        Save
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setSavingPreset(false)}>
                        Cancel
                      </Button>
                    </span>
                  ) : (
                    <button
                      onClick={() => setSavingPreset(true)}
                      className="pb-2.5 text-xs text-link hover:underline"
                    >
                      Save these choices as a preset
                    </button>
                  )}
                </div>
              )}
              {!savingPreset && (presets.data?.items?.length ?? 0) === 0 && (
                <button onClick={() => setSavingPreset(true)} className="text-xs text-link hover:underline">
                  Save these choices as a preset for next time
                </button>
              )}
              {presetNotes.length > 0 && (
                <ul className="space-y-1 rounded-lg border border-warn/40 bg-warn-soft px-4 py-3 text-sm text-warn">
                  {presetNotes.map((note) => (
                    <li key={note}>{note}</li>
                  ))}
                </ul>
              )}

              <div className="rounded-lg border border-line p-4">
                <div className="grid gap-4 sm:grid-cols-2">
                  <Field label="OLT">
                    <Input value={oltName} disabled />
                  </Field>
                  <Field label="PON port">
                    <Input value={target.ponPort} disabled className="font-mono" />
                  </Field>
                </div>
                <div className="mt-3">
                  <div className="flex items-center justify-between text-xs text-muted">
                    <span>Port usage</span>
                    <span className="tabular-nums">
                      {used} / {capacity} used · {free} free
                    </span>
                  </div>
                  <div className="mt-1 h-2 overflow-hidden rounded-full bg-surface-2">
                    <div
                      className={pct > 90 ? 'h-full bg-danger' : pct > 75 ? 'h-full bg-warn' : 'h-full bg-success'}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </div>
                <div className="mt-3">
                  <Field
                    label="ONU id"
                    hint={idTaken ? undefined : `${target.suggestedOnuId} is the next free id the chassis reported.`}
                    error={
                      idTaken
                        ? `${target.ponPort}:${onuId} is already configured — authorising onto it would overwrite that subscriber.`
                        : undefined
                    }
                  >
                    <div className="flex h-9 items-center rounded-lg border border-line bg-surface-2 px-3 font-mono text-sm text-ink">
                      {onuId}
                    </div>
                  </Field>
                </div>
              </div>
            </>
          )}

          {current.key === 'type' && (
            <div className="grid gap-4 rounded-lg border border-line p-4 sm:grid-cols-2">
              <Field label="ONU type" hint={typeHint}>
                <select
                  className="input"
                  value={onuType}
                  onChange={(e) => {
                    setOnuType(e.target.value);
                    setTypeTouched(true);
                    setCustomType('');
                  }}
                >
                  <option value="">— select a model —</option>
                  {chassisTypes
                    ? chassisTypes.map((t) => (
                        <option key={t} value={t}>
                          {t}
                        </option>
                      ))
                    : (models.data?.items ?? [])
                        .slice()
                        .sort((a, b) => a.name.localeCompare(b.name))
                        .map((m) => (
                          <option key={m.id} value={m.name}>
                            {m.name}
                          </option>
                        ))}
                </select>
              </Field>
              <Field
                label="Or type it by hand"
                hint={chassisTypes ? 'For a type defined on the chassis since this list was read' : 'For a model the library has not met yet'}
              >
                <Input
                  value={customType}
                  onChange={(e) => setCustomType(e.target.value)}
                  placeholder="F670LV9.0"
                  className="font-mono"
                />
              </Field>
            </div>
          )}

          {current.key === 'service' && (
            <div className="space-y-4">
              {speedItems.length > 0 && (
                <div className="grid gap-4 rounded-lg border border-line p-4 sm:grid-cols-2">
                  {(
                    [
                      ['up', 'Upload speed', upProfile, setUpProfile],
                      ['down', 'Download speed', downProfile, setDownProfile],
                    ] as const
                  ).map(([direction, label, value, set]) => {
                    const options = speedItems.filter((p) => p.direction === direction);
                    const chosen = options.find((p) => p.id === value);
                    const newHere = chosen && !chosen.writtenTo.some((w) => w.oltId === oltId);
                    return (
                      <Field
                        key={direction}
                        label={label}
                        hint={
                          newHere
                            ? 'This OLT does not hold it yet — it will be created there first'
                            : 'A package you sell'
                        }
                      >
                        <select className="input" value={value} onChange={(e) => set(e.target.value)}>
                          <option value="">The chassis default</option>
                          {options.map((p) => (
                            <option key={p.id} value={p.id}>
                              {p.name}
                              {speedLabel(p.maxKbps)}
                            </option>
                          ))}
                        </select>
                      </Field>
                    );
                  })}
                </div>
              )}
              <div className="grid gap-4 rounded-lg border border-line p-4 sm:grid-cols-2">
                <Field
                  label="Subscriber VLAN"
                  hint={
                    subscriberVlans.length === 0
                      ? 'This OLT carries no VLAN a subscriber may use — check the OLT’s VLANs tab'
                      : 'The VLAN their traffic rides'
                  }
                >
                  <select className="input" value={dataVlan} onChange={(e) => setDataVlan(e.target.value)}>
                    {subscriberVlans.map((v) => (
                      <option key={v.id} value={v.vlanId}>
                        {v.vlanId}
                        {v.description ? ` — ${v.description}` : ''}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field
                  label="Mode"
                  hint={
                    vlanMode === 'bridge'
                      ? 'The subscriber’s own router terminates the connection'
                      : 'The ONU terminates it and serves the LAN behind it'
                  }
                >
                  <select className="input" value={vlanMode} onChange={(e) => setVlanMode(e.target.value as VlanMode)}>
                    <option value="route">Routing</option>
                    <option value="bridge">Bridging</option>
                  </select>
                </Field>
                {vlanMode === 'route' && (
                <Field
                  label="Addressing"
                  hint={
                    addressing === 'dhcp'
                      ? 'The network hands out the address'
                      : addressing === 'static'
                        ? 'Set the address on the ONU’s page once it is up'
                        : 'The ONU dials with the account below, or one set on its page later'
                  }
                >
                  <select
                    className="input"
                    value={addressing}
                    onChange={(e) => setAddressing(e.target.value as VlanAddressing)}
                  >
                    <option value="dhcp">DHCP</option>
                    <option value="static">Static</option>
                    <option value="pppoe">PPPoE</option>
                  </select>
                </Field>
                )}
              </div>
              {effectiveAddressing === 'pppoe' && (
                <div className="grid gap-4 rounded-lg border border-line p-4 sm:grid-cols-2">
                  <Field label="PPPoE username" hint="Optional — the subscriber's account">
                    <Input
                      value={pppoeUser}
                      onChange={(e) => setPppoeUser(e.target.value)}
                      placeholder="subscriber@isp"
                      autoComplete="off"
                    />
                  </Field>
                  <Field label="PPPoE password" hint={accountHalf ? 'Both halves, or leave both empty' : 'Stored encrypted, never shown back'}>
                    <Input
                      type="password"
                      value={pppoePass}
                      onChange={(e) => setPppoePass(e.target.value)}
                      autoComplete="new-password"
                    />
                  </Field>
                  <p className="text-xs text-muted sm:col-span-2">
                    With the account, a Huawei ONT gets its PPPoE WAN created the first time it informs, LAN bound
                    behind it. Without it, the account is asked for on the ONU’s page once it is up.
                  </p>
                </div>
              )}
              {isC650 && vlanMode === 'route' && c650RoutedNotice}
            </div>
          )}

          {current.key === 'mgmt' && (
            <div className="space-y-4">
            <div className="grid gap-4 rounded-lg border border-line p-4 sm:grid-cols-2">
              <Field label="Management VLAN" hint="How SimpleACS will reach this ONU">
                <select
                  className="input"
                  value={mgmtVlan}
                  onChange={(e) => {
                    setMgmtVlan(e.target.value);
                    setMgmtTouched(true);
                  }}
                >
                  {managementVlans.map((v) => (
                    <option key={v.id} value={v.vlanId}>
                      {v.vlanId}
                      {v.description ? ` — ${v.description}` : ''}
                    </option>
                  ))}
                </select>
              </Field>
              <Field
                label="Managed by"
                hint={
                  managementMode === 'omci'
                    ? 'The ONU is taken off TR-069 right after it is authorised'
                    : 'The ONU is pointed at your ACS right after it is authorised'
                }
                error={
                  managementMode === 'tr069' && !mgmtVlan
                    ? 'TR-069 needs the management VLAN above — without one the chassis accepts it and the ONU never informs.'
                    : undefined
                }
              >
                <select
                  className="input"
                  value={managementMode}
                  onChange={(e) => setManagementMode(e.target.value as 'tr069' | 'omci')}
                >
                  <option value="tr069">TR-069</option>
                  <option value="omci">OMCI only</option>
                </select>
              </Field>
            </div>
            </div>
          )}

          {current.key === 'location' && zones.length > 0 && (
            <div className="grid gap-4 rounded-lg border border-line p-4 sm:grid-cols-2">
              <Field label="Zone" hint="The service area — narrows the splitters">
                <select
                  className="input"
                  value={zoneId}
                  onChange={(e) => {
                    setZoneId(e.target.value);
                    setSplitterId('');
                    setSplitterPort('');
                  }}
                >
                  <option value="">Not recorded</option>
                  {zones.map((z) => (
                    <option key={z.id} value={z.id}>
                      {z.name}
                    </option>
                  ))}
                </select>
              </Field>
              <Field
                label="Splitter / ODB"
                hint={zoneId ? `${splitters.length} in this zone` : 'Pick a zone to narrow'}
              >
                <select
                  className="input"
                  value={splitterId}
                  onChange={(e) => {
                    const sp = splitters.find((s) => s.id === e.target.value);
                    setSplitterId(e.target.value);
                    setSplitterPort('');
                    if (sp?.zoneId && !zoneId) setZoneId(sp.zoneId);
                  }}
                >
                  <option value="">Not recorded</option>
                  {splitters.map((sp) => (
                    <option key={sp.id} value={sp.id}>
                      {sp.name} · 1:{sp.capacity} · {sp.deviceCount ?? 0}/{sp.capacity} used
                    </option>
                  ))}
                </select>
              </Field>
              {chosenSplitter && (
                <div className="sm:col-span-2">
                  <Field label="Splitter port" hint="Which output the drop hangs off — taken ports are not offered">
                    <SplitterPortGrid
                      splitterId={chosenSplitter.id}
                      capacity={chosenSplitter.capacity}
                      value={splitterPort}
                      onChange={setSplitterPort}
                    />
                  </Field>
                </div>
              )}
            </div>
          )}

          {current.key === 'review' && (
            <div className="space-y-4">
              <Field label="Name" hint="What the subscriber is called on the chassis">
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Jane Wanjiru - 4821"
                />
              </Field>

              <div className="flex flex-wrap gap-2">
                <Pill tone="info">{target.ponPort}:{onuId}</Pill>
                <Pill state tone={chosenType ? 'neutral' : 'warn'}>{chosenType || 'no model chosen'}</Pill>
                <Pill state tone={dataVlan ? 'neutral' : 'warn'}>
                  {dataVlan ? `VLAN ${dataVlan}` : 'no subscriber VLAN'}
                </Pill>
                {mgmtVlan && <Pill tone="neutral">mgmt VLAN {mgmtVlan}</Pill>}
                <Pill tone="neutral">{vlanMode === 'bridge' ? 'bridged' : 'routed'}</Pill>
                {vlanMode === 'route' && (
                  <Pill tone="neutral">
                    {addressing === 'dhcp' ? 'DHCP' : addressing === 'static' ? 'static' : accountGiven ? `PPPoE as ${pppoeUser.trim()}` : 'PPPoE'}
                  </Pill>
                )}
                {managementMode && <Pill tone="info">{managementMode === 'tr069' ? 'TR-069' : 'OMCI only'}</Pill>}
              </div>

              {isC650 && vlanMode === 'route' && c650RoutedNotice}

              <div className="rounded-lg border border-line bg-surface-2 px-4 py-3 text-sm">
                <p className="font-medium text-ink">
                  Authorise {target.serial} as {target.ponPort}:{onuId}
                </p>
                <p className="mt-0.5 text-muted">
                  {chosenType || 'Choose the ONU type'}
                  {dataVlan ? ` on VLAN ${dataVlan}` : ''}
                  {mgmtVlan ? `, managed on VLAN ${mgmtVlan}` : ''}, {vlanMode === 'bridge' ? 'bridged' : 'routed'}
                  {vlanMode === 'bridge'
                    ? ''
                    : addressing === 'dhcp' ? ' with DHCP' : addressing === 'static' ? ' with a static address' : accountGiven ? ` over PPPoE as ${pppoeUser.trim()}` : ' over PPPoE'}
                  {upProfile || downProfile ? ', shaped by the speed profiles chosen' : ''}. The OLT is
                  written in the background and you will land on the subscriber's page
                  {effectiveAddressing === 'dhcp' || accountGiven ? '' : ', where the address details are filled in'}.
                  {managementMode && (
                    <>
                      {' '}
                      Once that lands, a second write puts it on{' '}
                      {managementMode === 'tr069' ? 'TR-069' : 'OMCI only'}. If the chassis refuses that
                      part the ONU still stays in service, and its page says so.
                    </>
                  )}
                </p>
              </div>
            </div>
          )}

          {error && <ErrorNotice error={new Error(error)} />}
          {elsewhere && (
            <div className="rounded-lg border border-warn/40 bg-warn-soft px-4 py-3 text-sm">
              <p className="text-ink">{elsewhere}</p>
              <div className="mt-3 flex flex-wrap gap-2">
                <Button
                  variant="primary"
                  loading={apply.isPending}
                  onClick={() => apply.mutate({ deauthorizeElsewhere: true })}
                >
                  Authorise afresh
                </Button>
                <Button variant="ghost" onClick={() => setElsewhere(null)}>
                  Keep it where it is
                </Button>
              </div>
            </div>
          )}
        </div>

        <footer className="flex items-center justify-between gap-2 border-t border-line px-5 py-4">
          <Button variant="ghost" onClick={goBack}>
            {step === 0 ? 'Cancel' : 'Back'}
          </Button>
          {current.key !== 'review' ? (
            <Button variant="primary" disabled={!canAdvance} onClick={() => setStep((s) => Math.min(s + 1, steps.length - 1))}>
              Next
            </Button>
          ) : (
            <Button
              variant="primary"
              disabled={idTaken || !chosenType || !dataVlan || tr069NeedsVlan}
              loading={apply.isPending}
              onClick={() => apply.mutate({})}
              title={
                !chosenType
                  ? 'Choose the ONU type first'
                  : !dataVlan
                    ? 'Choose the VLAN the subscriber rides'
                    : tr069NeedsVlan
                        ? 'TR-069 needs a management VLAN — pick one, or switch to OMCI only'
                        : undefined
              }
            >
              Authorise
            </Button>
          )}
        </footer>
      </div>
    </div>
  );
}
